import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  useUtils,
  PickersArrowSwitcher,
  usePreviousMonthDisabled,
  useNextMonthDisabled,
} from '@mui/x-date-pickers/internals';

// Replaces the stock header's single "September 2026" toggle with two separate
// targets: clicking the month opens the month grid, clicking the year opens the
// year grid, rather than cycling both through one shared label.
const labelSx = (isActive, disabled) => ({
  cursor: disabled ? 'default' : 'pointer',
  fontWeight: isActive ? 700 : 500,
  fontSize: '0.95rem',
  lineHeight: 1.4,
  color: isActive ? 'primary.main' : 'text.primary',
  userSelect: 'none',
  '&:hover': disabled ? undefined : { color: 'primary.main' },
});

const ReportsCalendarHeader = (props) => {
  const {
    currentMonth,
    disabled,
    disableFuture,
    disablePast,
    maxDate,
    minDate,
    onMonthChange,
    onViewChange,
    view,
    views,
    labelId,
  } = props;
  const utils = useUtils();

  const selectNextMonth = () => onMonthChange(utils.getNextMonth(currentMonth), 'left');
  const selectPreviousMonth = () => onMonthChange(utils.getPreviousMonth(currentMonth), 'right');
  const isNextMonthDisabled = useNextMonthDisabled(currentMonth, { disableFuture, maxDate });
  const isPreviousMonthDisabled = usePreviousMonthDisabled(currentMonth, { disablePast, minDate });

  // Clicking the already-open view returns to the day grid, so the label acts
  // as a toggle rather than trapping the user in the month/year grid.
  const switchTo = (target) => () => {
    if (disabled || !onViewChange) return;
    onViewChange(view === target ? 'day' : target);
  };

  const handleKeyDown = (target) => (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    switchTo(target)();
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ pl: 2.5, pr: 1.5, pt: 1.5, pb: 0.5, minHeight: 34 }}
    >
      <Stack direction="row" spacing={1} alignItems="center" id={labelId}>
        {views.includes('month') && (
          <Typography
            role="button"
            aria-label="Choose month"
            tabIndex={disabled ? -1 : 0}
            onClick={switchTo('month')}
            onKeyDown={handleKeyDown('month')}
            sx={labelSx(view === 'month', disabled)}
          >
            {utils.format(currentMonth, 'month')}
          </Typography>
        )}
        {views.includes('year') && (
          <Typography
            role="button"
            aria-label="Choose year"
            tabIndex={disabled ? -1 : 0}
            onClick={switchTo('year')}
            onKeyDown={handleKeyDown('year')}
            sx={labelSx(view === 'year', disabled)}
          >
            {utils.format(currentMonth, 'year')}
          </Typography>
        )}
      </Stack>
      {view === 'day' && (
        <PickersArrowSwitcher
          onGoToPrevious={selectPreviousMonth}
          onGoToNext={selectNextMonth}
          isPreviousDisabled={isPreviousMonthDisabled}
          isNextDisabled={isNextMonthDisabled}
          previousLabel="Previous month"
          nextLabel="Next month"
        />
      )}
    </Stack>
  );
};

export default ReportsCalendarHeader;
